import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { OpenIncidentForm } from "@/components/qc/OpenIncidentForm";
import { cn } from "@/lib/utils";

import type { ConfigurationBuiltUnit } from "./configurationApi";

function formatAssembledAt(value: string | null): string {
  if (value == null || value.trim() === "") return "—";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleString();
}

function unitMatchesFilter(unit: ConfigurationBuiltUnit, needle: string): boolean {
  const haystack = [
    unit.deviceLabelSerial,
    unit.pcbaSerial,
    unit.clampSerial,
    unit.mcuUuid,
    String(unit.serialFciId),
  ]
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

export function ConfigurationBuiltUnitsPanel({
  units,
  loading,
  errorMessage,
  writesAllowed,
  reconfigureError,
  filterText,
  rescanSerialFciId,
  onRescan,
}: {
  units: ConfigurationBuiltUnit[];
  loading: boolean;
  errorMessage: string | null;
  writesAllowed: boolean;
  reconfigureError: string | null;
  filterText: string;
  rescanSerialFciId: number | null;
  onRescan: (unit: ConfigurationBuiltUnit) => void;
}): JSX.Element | null {
  const [qcIncidentUnit, setQcIncidentUnit] = useState<ConfigurationBuiltUnit | null>(null);

  const filteredUnits = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (needle === "") return units;
    return units.filter((unit) => unitMatchesFilter(unit, needle));
  }, [filterText, units]);

  useEffect(() => {
    if (qcIncidentUnit == null) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setQcIncidentUnit(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [qcIncidentUnit]);

  if (loading) {
    return (
      <p className="px-4 py-4 text-sm text-slate-500">
        <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden />
        Loading configured units…
      </p>
    );
  }

  if (errorMessage) {
    return <p className="px-4 py-4 text-sm text-red-700">{errorMessage}</p>;
  }

  if (units.length === 0) {
    return <p className="px-4 py-4 text-sm text-slate-500">No units configured for this job yet.</p>;
  }

  return (
    <>
      {reconfigureError ? (
        <p className="px-4 pb-3 pt-4 text-sm text-red-700" role="alert">
          {reconfigureError}
        </p>
      ) : null}

      <div>
        {filteredUnits.length === 0 ? (
          <p className="px-4 py-4 text-sm text-slate-500">No units match this filter.</p>
        ) : (
          <div className="overflow-x-auto border-t border-slate-200">
            <table className="min-w-[960px] w-full border-collapse text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Device label</th>
                <th className="px-3 py-2">PCBA serial</th>
                <th className="px-3 py-2">Clamp serial</th>
                <th className="px-3 py-2">MCU UUID</th>
                <th className="px-3 py-2">Configured</th>
                <th className="px-3 py-2">serial_fci</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUnits.map((unit) => {
                const rescanActive = rescanSerialFciId === unit.serialFciId;
                return (
                <tr
                  key={unit.serialFciId}
                  className={cn(
                    "text-slate-900",
                    rescanActive && "bg-amber-50 ring-1 ring-inset ring-amber-300",
                  )}
                >
                  <td className="px-3 py-2 font-mono text-xs">{unit.deviceLabelSerial}</td>
                  <td className="px-3 py-2 font-mono text-xs">{unit.pcbaSerial ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{unit.clampSerial ?? "—"}</td>
                  <td className="max-w-[14rem] px-3 py-2 font-mono text-xs break-all">{unit.mcuUuid ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                    {formatAssembledAt(unit.dateAssembled)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs tabular-nums">{unit.serialFciId}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-9 touch-manipulation px-2.5 text-xs"
                        disabled={!writesAllowed}
                        onClick={() => setQcIncidentUnit(unit)}
                      >
                        QC incident
                      </Button>
                      <Button
                        type="button"
                        variant={rescanActive ? "primary" : "secondary"}
                        className={cn(
                          "h-9 touch-manipulation px-2.5 text-xs",
                          rescanActive &&
                            "bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-400 disabled:bg-amber-500/60",
                        )}
                        disabled={!writesAllowed}
                        onClick={() => onRescan(unit)}
                      >
                        {rescanActive ? "Rescan armed" : "Rescan"}
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {qcIncidentUnit
        ? createPortal(
            <div
              {...hubModalOverlayProps(false)}
              role="dialog"
              aria-modal="true"
              aria-labelledby="configuration-qc-incident-title"
            >
              <div className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl">
                <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
                  <h2 id="configuration-qc-incident-title" className="text-lg font-semibold text-slate-900">
                    Open QC incident
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Device label {qcIncidentUnit.deviceLabelSerial} · serial_fci {qcIncidentUnit.serialFciId}
                  </p>
                </div>
                <div className="px-4 py-4 sm:px-5">
                  <OpenIncidentForm
                    defaultType="device"
                    serialPrefill={qcIncidentUnit.deviceLabelSerial}
                    defaultIssue="Configuration issue"
                    defaultIssueNotes={`Configuration job — serial_fci ${qcIncidentUnit.serialFciId}, PCBA ${qcIncidentUnit.pcbaSerial ?? "unknown"}.`}
                    onCancel={() => setQcIncidentUnit(null)}
                    onSuccess={() => setQcIncidentUnit(null)}
                  />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
